import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { BannerComponent } from '../components/banner.component';
import { ConfigurableButtonComponent } from '../components/button.component';
import { ProgressTrackerComponent } from '../components/progress-tracker.component';
import { TableComponent } from '../components/table.component';
import { PUITranslatePipe } from '../pipes/p-ui-translate.pipe';
import { BulkOperationService } from '../services/bulk-operation.service';
import { NavigationService } from '../services/navigation.service';
import { ReassignmentService } from '../services/reassignment.service';
import { TableConfigBuilder } from '../services/table-config-builder.service';

declare var ap: any;

interface Task {
    processId: number;
    containerId: string;
    taskId: string;
    cid: string;
    assignedTo: string;
    assignmentStatus: string;
    status?: 'completed' | 'failed' | 'in-progress' | string;
}

interface TaskSummaryInput {
    summary: Task[];
}

interface TaskSummaryOutput {
    totalCount: number;
    remainingCount: number;
    states: {
        completed: number;
        failed: number;
        inProgress: number;
    };
}

interface BannerStyleObject {
    iconColor: string;
    backgroundColor: string;
    titleColor: string;
    borderColor: string;
    descriptionColor: string;
}

@Component({
    selector: 'app-bulk-operation-tracking',
    standalone: true,
    template: `
        <div class="w-full h-full flex-column shadow-md surface-ground flex-1 flex overflow-auto">
            <!-- Banner Section -->
            @if (showSummaryTable) {
                <platform-banner
                    class="px-3 pt-3"
                    [icon]="bannerIcon"
                    [title]="bannerTitle | pTranslate"
                    [description]="bannerMessage | pTranslate"
                    [appliedStyle]="bannerStyleObject"
                    [totalCount]="bannerTotalCount"
                    [currentCount]="bannerCurrentCount"
                    [additionalText]="'default-reassign-summary-additionalText' | pTranslate"
                    [taskSummaryText]="
                        'default-reassign-summary-text'
                            | pTranslate
                                : bannerCurrentCount + 'out of' + bannerTotalCount + 'tasks were assigned.'
                                : { currentCount: bannerCurrentCount, totalCount: bannerTotalCount }
                    "
                >
                </platform-banner>
            }
            <div class="flex flex-column flex-1 px-3 ">
                <!-- Table Section -->
                @if (showSummaryTable) {
                    <div
                        class="mt-3 flex flex-column flex-1 gap-3 reassignment-cases overflow-auto border-1 surface-border border-round-sm"
                    >
                        <div class="border-bottom-1 surface-border p-3 font-semibold">
                            {{ 'default-reassign-bulkReassignmentTableTitle' | pTranslate: 'Failed allocation:' }} ({{
                                failed
                            }})
                        </div>
                        <platform-table
                            [data]="tableData"
                            [tableConfiguration]="tableConfig.tableConfiguration"
                            [selectable]="tableConfig.selectable"
                            [customClass]="'reassignment-tracking-table ' + reassignmentService.currentTableClass"
                        >
                        </platform-table>
                    </div>
                }
            </div>

            <!-- re-attempt button section -->
            <div class="w-full surface-ground border-top-1 surface-border p-3 bottom-0 sticky">
                @if (showSummaryTable) {
                    <div class="flex justify-content-end gap-3">
                        <platform-button
                            [buttonType]="'button'"
                            [outlined]="true"
                            [label]="'default-reassign-buttonCancel' | pTranslate: 'Cancel'"
                            (click)="cancel()"
                        ></platform-button>
                        <platform-button
                            [buttonType]="'button'"
                            [label]="'default-reassign-buttonRetryAllocation' | pTranslate: 'Retry Allocation'"
                            (click)="reattempt()"
                        ></platform-button>
                    </div>
                }
            </div>

            <!-- Progress Tracker Section -->
            @if (!showSummaryTable) {
                <div class="flex flex-column justify-content-center align-items-center h-full w-full gap-3">
                    <platform-progress-tracker
                        [header]="header | pTranslate"
                        [subtitle]="subtitle | pTranslate"
                        [total]="total"
                        [current]="current"
                        [disableRoute]="disableRoute"
                        [statusImage]="statusImage"
                    >
                    </platform-progress-tracker>
                    @if (isCompleted) {
                        <platform-button
                            [buttonType]="'button'"
                            [label]="'default-reassign-buttonBackToInbox' | pTranslate"
                            [styleClass]="'reassign-action-btn'"
                            [outlined]="true"
                            [icon]="'pi pi-angle-left'"
                            [iconPosition]="'left'"
                            (buttonClick)="navigationService.navigateToInbox()"
                        ></platform-button>
                    }
                </div>
            }
        </div>
    `,
    imports: [ProgressTrackerComponent, TableComponent, BannerComponent, ConfigurableButtonComponent, PUITranslatePipe],
    styles: [
        `
            :host {
                display: block;
                height: 100%;
            }
        `,
    ],
})
export class BulkOperationTrackingComponent implements OnDestroy, OnInit {
    reassignmentService = inject(ReassignmentService);
    private bulkOperationService = inject(BulkOperationService);
    public navigationService = inject(NavigationService);
    private confirmationService = inject(ConfirmationService);
    private pollingSubscription: Subscription | null = null;
    private imageBaseUrl = 'assets/images/reassignment/';
    private messageService = inject(MessageService);
    total = 0;
    failed = 0;
    current = 0;
    header = '';
    subtitle = '';
    disableRoute = false;
    statusImage = 'in-progress.png';

    showSummaryTable = false; // Toggles between tracker and summary table
    tableData: any[] = []; // Data for the platform-table
    tableInstance: TableConfigBuilder | null = null;
    tableConfig: any;

    bannerTitle = 'default-reassign-bulkReassignmentBannerTitleInProgress';
    bannerIcon = 'pi pi-users';
    bannerMessage = '';
    bannerCurrentCount = 0;
    bannerTotalCount = 0;

    bannerStyleObject: BannerStyleObject = {
        iconColor: 'var(--red-500)',
        backgroundColor: 'var(--surface-hover)',
        borderColor: 'var(--surface-border)',
        titleColor: 'var(--alpha-text-inactive-color)',
        descriptionColor: 'var(--alpha-text-inactive-color)',
    };
    isCompleted = false;

    ngOnInit() {
        if (this.reassignmentService.casesToReassign.length === 0) {
            this.navigationService.navigateToInbox();
        }

        this.statusImage = this.imagePath('in-progress.svg');
        this.startingShortly();
        const operationId = this.reassignmentService?.operationId;
        this.pollingSubscription = this.bulkOperationService
            .pollOperationSummary(operationId)
            .subscribe((summary: any) => {
                this.isCompleted = this.updateProgressTracker(summary);
                if (!this.isCompleted) {
                    this.checkCompletion(summary);
                } else {
                    this.reassignmentService.casesToReassign = [];
                    this.unsubscribeFromPolling();
                }
            });
    }

    ngOnDestroy() {
        this.unsubscribeFromPolling();
    }

    calculateTaskSummary(data: TaskSummaryInput): TaskSummaryOutput {
        const summary = data.summary || [];

        const totalCount = summary.length;
        let completedCount = 0;
        let failedCount = 0;
        let inProgressCount = 0;

        summary.forEach((task) => {
            switch (task.status?.toLowerCase()) {
                case 'completed':
                    completedCount++;
                    break;
                case 'failed':
                    failedCount++;
                    break;
                case 'in_progress':
                    inProgressCount++;
                    break;
            }
        });
        return {
            totalCount,
            remainingCount: totalCount - (completedCount + failedCount),
            states: {
                completed: completedCount,
                failed: failedCount,
                inProgress: inProgressCount,
            },
        };
    }

    imagePath(imageName: string): string {
        return this.imageBaseUrl + imageName;
    }

    startingShortly() {
        this.header = 'default-reassign-bulkReassignmentInProgress';
        this.subtitle = 'default-reassign-bulkReassignmentInProgressSubtitle';
        this.disableRoute = true;
        this.statusImage = this.imagePath('in-progress.svg');
    }

    updateProgressTracker(data: TaskSummaryInput): boolean {
        let success = false;
        const taskSummary = this.calculateTaskSummary(data);
        this.total = taskSummary.totalCount;
        this.current = taskSummary.states.completed + taskSummary.states.failed;
        if (taskSummary.states.completed === this.total) {
            // All tasks are completed
            success = true;
            this.header = 'default-reassign-bulkReassignmentComplete';
            this.subtitle = 'default-reassign-bulkReassignmentCompleteSubtitle';
            this.disableRoute = false;
            this.statusImage = this.imagePath('completed.png');
        } else if (taskSummary.states.completed === 0 && taskSummary.states.failed === 0) {
            // No progress yet
            this.header = 'default-reassign-bulkReassignmentInProgress';
            this.subtitle = 'default-reassign-bulkReassignmentInProgressSubtitle';
            this.disableRoute = true;
            this.statusImage = this.imagePath('in-progress.svg');
        } else if (
            taskSummary.states.failed > 0 &&
            taskSummary.states.completed + taskSummary.states.failed === this.total
        ) {
            // Some tasks failed
            this.header = 'default-reassign-bulkReassignmentFailed';
            this.subtitle = 'default-reassign-bulkReassignmentFailedSubtitle';
            this.disableRoute = false;
            this.statusImage = this.imagePath('in-progress.svg');
        } else {
            // Partial progress
            this.header = 'default-reassign-bulkReassignmentInProgress';
            this.subtitle = 'default-reassign-bulkReassignmentInProgressSubtitle';
            this.disableRoute = true;
            this.statusImage = this.imagePath('in-progress.svg');
        }
        return success;
    }

    async checkCompletion(data: TaskSummaryInput) {
        const taskSummary = this.calculateTaskSummary(data);
        // // Initialize the state variable to ensure it's always assigned
        let state: 'complete' | 'partially-complete' | 'failed' | '' = ''; // Default value

        if (taskSummary.states.completed === taskSummary.totalCount) {
            state = 'complete';
        } else if (taskSummary.states.failed === taskSummary.totalCount) {
            state = 'failed';
        } else if (taskSummary.states.completed + taskSummary.states.failed === taskSummary.totalCount) {
            state = 'partially-complete';
        }

        if (state === 'complete' || state === 'failed' || state === 'partially-complete') {
            this.unsubscribeFromPolling();
            this.setBannerTitle(state, taskSummary);
            this.tableInstance = this.reassignmentService.buildAfterReassignmentSummaryTable(data.summary as any);
            this.tableConfig = this.tableInstance.build();
            this.tableData = (await this.tableInstance.view()).data;
            this.showSummaryTable = true;
        }
    }

    setBannerTitle(state: 'complete' | 'partially-complete' | 'failed' | '', taskSummary: TaskSummaryOutput) {
        this.bannerTotalCount = taskSummary.totalCount;
        this.bannerCurrentCount = taskSummary.states.completed;
        if (state === 'complete') {
            this.bannerTitle = 'default-reassign-bulkReassignmentBannerTitleComplete';
            this.bannerIcon = 'pi pi-users';
        } else if (state === 'partially-complete') {
            this.failed = taskSummary.states.failed;
            this.bannerMessage = 'default-reassign-bulkReassignmentBannerMessagePartiallyComplete';
            this.bannerTitle = 'default-reassign-bulkReassignmentBannerTitlePartiallyComplete';
            this.bannerIcon = 'pi pi-exclamation-triangle';
            this.bannerStyleObject.iconColor = 'var(--orange-500)';
        } else if (state === 'failed') {
            // error icon
            this.failed = taskSummary.states.failed;
            this.bannerIcon = 'pi pi-exclamation-circle';
            this.bannerMessage = 'default-reassign-bulkReassignmentBannerMessageFailed';
            this.bannerTitle = 'default-reassign-bulkReassignmentBannerTitleFailed';
        }
    }

    cancel() {
        this.confirmationService.confirm({
            message: ap.language.translateConfig('{{ lang.default-reassign-snackbarReassignmentCancelMessage }}') || '',
            header:
                ap.language.translateConfig('{{ lang.default-reassign-snackbarReassignmentCancelHeader }}') ||
                'default-reassign-snackbarReassignmentCancelHeader',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.navigationService.navigateToInbox();
            },
        });
    }

    reattempt() {
        //Reset state for reattempt
        this.showSummaryTable = false; // Show progress tracker
        this.header = 'default-reassign-bulkReassignmentInProgress';
        this.subtitle = 'default-reassign-bulkReassignmentInProgressSubtitle';
        this.disableRoute = true;
        this.statusImage = this.imagePath('in-progress.svg');
        this.total = 0;
        this.current = 0;

        // Unsubscribe from any existing polling
        this.unsubscribeFromPolling();

        // Trigger a new bulk reassignment request
        const bulkReassignmentRequest = this.reassignmentService.formatReassignmentDataForBulkOperation(true);
        this.bulkOperationService.performBulkReassignment(bulkReassignmentRequest).subscribe({
            next: (data) => {
                // Set the new operation ID
                this.reassignmentService.operationId = data.id;

                // Restart polling for the new operation
                this.pollingSubscription = this.bulkOperationService
                    .pollOperationSummary(data.id)
                    .subscribe((summary: any) => {
                        this.isCompleted = this.updateProgressTracker(summary);
                        if (!this.isCompleted) {
                            this.checkCompletion(summary);
                        } else this.unsubscribeFromPolling();
                    });
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    detail: error?.error?.message,
                    summary: 'Error',
                });
            },
        });
    }

    unsubscribeFromPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
        }
    }
}
